import React, { useState, useContext } from 'react';

// Styles & Assets
import styles from './FilterDialog.module.css';
import { ReactComponent as FilterSVG } from '../../../assets/icons/filter-off-fill.svg';

// Context
import FiltersContext from '../../../context/FiltersContext';

// Hooks
import { useTranslation } from 'react-i18next';

// Components
import RowButtonsFilter from '../RowButtonsFilter/RowButtonsFilter';
import MultiRangeSlider from '../../UI/MultiRangeSlider/MultiRangeSlider';

// Constants
const MIN_STAY_DAYS = 0;
const MAX_STAY_DAYS = 10;

const FilterDialog: React.FC<{
    modalShow: React.Dispatch<React.SetStateAction<boolean>>;
    setFilterBallActive: (isActive: boolean) => void;
}> = ({ modalShow, setFilterBallActive }) => {

    const { profiles, patientFilters, dispatchPatientsFilters } = useContext(FiltersContext);

    const { t } = useTranslation();

    const [profileIDs, setProfileIDs] = useState<number[]>(
        patientFilters.currentProfileID !== null ? [patientFilters.currentProfileID] : []
    );
    const [entryDaysRange, setEntryDaysRange] = useState<{ min: number; max: number | null }>(
        patientFilters.daysEntryRange
    );

    const handleApplyFilters = () => {
        dispatchPatientsFilters({ type: 'PROFILE', payload: profileIDs });
        dispatchPatientsFilters({ type: 'DAYS_ENTRY', payload: entryDaysRange });

        modalShow(false);

        setFilterBallActive(
            profileIDs.length !== 0 ||
            entryDaysRange.min !== 0 ||
            entryDaysRange.max !== null
        );
    };

    const deleteFilters = () => {
        setProfileIDs([]);
        setEntryDaysRange({ min: 0, max: null });
    };

    const departmentFiltersContent = profiles.map(profilesZones => ({
        id: profilesZones.id,
        content: profilesZones.name || ''
    }));

    const handleClickEnvironment = (id: number) => {
        setProfileIDs(oldIDs =>
            oldIDs.includes(id)
                ? oldIDs.filter(oldID => oldID !== id)
                : [...oldIDs, id]
        );
    };

    const handleChangeEntry = ({ min, max }: { min: number; max: number }) => {
        setEntryDaysRange({
            min,
            max: max === MAX_STAY_DAYS ? null : max
        });
    };

    return (
        <div className={`${styles.filterDialogContainer} dialog`}>
            <div className={styles.filterOptionsContainer}>
                <div className={styles.filterOptions}>
                    <div className={styles.topOptions}>
                        <div>
                            <h1 className='dialogTitle'>{t('filter')}</h1>
                            <h2 className='dialogSubTitle'>{t('patients_list')}</h2>
                        </div>
                        <button onClick={deleteFilters} className='buttonRemoveFilter'>
                            {t('remove_filters')} <FilterSVG />
                        </button>
                    </div>

                    <div className={styles.rowFilters}>
                        <RowButtonsFilter
                            text={t('environment')}
                            currentFilters={profileIDs}
                            handleClick={handleClickEnvironment}
                            content={departmentFiltersContent}
                        />
                    </div>

                    <h3 className={styles.subtitle}>
                        {t('entry')} ({t('days')})
                    </h3>

                    <div className={styles.multiRangeContainer}>
                        <MultiRangeSlider
                            min={MIN_STAY_DAYS}
                            max={MAX_STAY_DAYS}
                            minValue={entryDaysRange.min}
                            maxValue={entryDaysRange.max ?? MAX_STAY_DAYS}
                            labelMax={(max) => max === MAX_STAY_DAYS ? max + '+' : max.toString()}
                            onChange={handleChangeEntry}
                        />
                    </div>
                </div>
            </div>

            <div className={styles.bottomButtonContainer}>
                <button onClick={handleApplyFilters} className={styles.bottomButton}>
                    {t('filter')}
                </button>
            </div>
        </div>
    );
};

export default FilterDialog;